/**
 * Country -> currency sync (Maryam's / Impulse)
 * ---------------------------------------------------------------------------
 * Every Shopify Market country currently prices in PKR, so the display currency
 * is driven client-side by the BA currency converter (Nova / Booster, "bacurr"),
 * based on the country chosen in the Orbe geolocation popup.
 *
 *  1. Header button (.js-ccs-open) opens the Orbe popup (works after it was dismissed).
 *  2. On load: country (html[data-country], from Liquid localization) -> currency
 *     -> switch the (hidden) BA widget if it differs.
 *  3. Re-runs BA conversion after AJAX renders (cart drawer, quick view, search...).
 *
 * Only theme code; Orbe and BA app files are not touched. Every entry point is
 * wrapped in try/catch so a missing app never breaks the header.
 *
 * TODO: remove when Markets currencies enabled
 *   (delete this file + its <script>/<style> block in layout/theme.liquid,
 *    snippets/country-currency-button.liquid and its two renders).
 */
(function () {
  'use strict';

  var ORBE_MODAL_ID = 'md-app-embed__modal';
  var STORAGE_KEY = 'ccs_country';      // backup of the country picked in Orbe
  var BA_SESSION_KEY = 'bacurr_user_cur'; // BA's own sessionStorage key (JSON string)
  var FALLBACK_CURRENCY = 'USD';         // used when BA doesn't offer a country's currency
  var WAIT_FOR_BA_MS = 10000;            // stop observing after this, no infinite polling
  var MAX_SWITCH_ATTEMPTS = 3;

  /* ---------- Country -> currency map --------------------------------------
     Currencies BA doesn't offer (e.g. XCD, XOF) fall back to USD at runtime,
     checked against the options in #baCurrSelector. */
  var SHARED = {
    USD: 'US UM PR GU VI AS MP BQ EC SV TC VG IO TL ZW PW FM MH',
    EUR: 'DE FR IT ES NL BE AT IE PT FI GR LU MT CY SK SI EE LV LT HR AD MC SM VA XK ME AX BL GF GP MF MQ PM RE YT TF',
    GBP: 'GB GG JE IM GS',
    AUD: 'AU CC CX KI NF NR TV',
    NZD: 'NZ CK NU PN TK',
    DKK: 'DK FO GL',
    NOK: 'NO SJ',
    CHF: 'CH LI',
    XAF: 'CF CG CM GA GQ TD',
    XOF: 'BF BJ CI GW ML NE SN TG',
    XCD: 'AG AI DM GD KN LC MS VC',
    XPF: 'NC PF WF',
    ILS: 'IL PS',
    MAD: 'MA EH',
    ANG: 'CW SX',
    SHP: 'SH TA AC'
  };
  var SINGLE = 'PK:PKR CA:CAD AE:AED SA:SAR QA:QAR KW:KWD BH:BHD OM:OMR IN:INR BD:BDT ' +
    'LK:LKR MY:MYR SG:SGD HK:HKD JP:JPY CN:CNY TR:TRY SE:SEK ZA:ZAR LS:LSL ' +
    'AF:AFN AL:ALL AM:AMD AO:AOA AR:ARS AW:AWG AZ:AZN BA:BAM BB:BBD BG:BGN BI:BIF ' +
    'BM:BMD BN:BND BO:BOB BR:BRL BS:BSD BT:BTN BW:BWP BY:BYN BZ:BZD CD:CDF CL:CLP ' +
    'CO:COP CR:CRC CV:CVE CZ:CZK DJ:DJF DO:DOP DZ:DZD EG:EGP ER:ERN ET:ETB FJ:FJD ' +
    'FK:FKP GE:GEL GH:GHS GI:GIP GM:GMD GN:GNF GT:GTQ GY:GYD HN:HNL HT:HTG HU:HUF ' +
    'ID:IDR IQ:IQD IS:ISK JM:JMD JO:JOD KE:KES KG:KGS KH:KHR KM:KMF KR:KRW KY:KYD ' +
    'KZ:KZT LA:LAK LB:LBP LR:LRD LY:LYD MD:MDL MG:MGA MK:MKD MM:MMK MN:MNT MO:MOP ' +
    'MR:MRU MU:MUR MV:MVR MW:MWK MX:MXN MZ:MZN NA:NAD NG:NGN NI:NIO NP:NPR PA:PAB ' +
    'PE:PEN PG:PGK PH:PHP PL:PLN PY:PYG RO:RON RS:RSD RU:RUB RW:RWF SB:SBD SC:SCR ' +
    'SD:SDG SL:SLL SO:SOS SR:SRD SS:SSP ST:STD SZ:SZL TH:THB TJ:TJS TM:TMT TN:TND ' +
    'TO:TOP TT:TTD TW:TWD TZ:TZS UA:UAH UG:UGX UY:UYU UZ:UZS VE:VEF VN:VND VU:VUV ' +
    'WS:WST YE:YER ZM:ZMW';

  var CURRENCY_BY_COUNTRY = {};
  Object.keys(SHARED).forEach(function (cur) {
    SHARED[cur].split(' ').forEach(function (cc) { CURRENCY_BY_COUNTRY[cc] = cur; });
  });
  SINGLE.split(' ').forEach(function (pair) {
    var p = pair.split(':');
    CURRENCY_BY_COUNTRY[p[0]] = p[1];
  });

  /* ---------- Small helpers ------------------------------------------------ */
  var root = document.documentElement;
  var shopCurrency = (root.getAttribute('data-shop-currency') || 'PKR').toUpperCase();

  function safe(fn) {
    return function () {
      try { return fn.apply(this, arguments); } catch (e) {
        if (window.console) console.warn('[country-currency-sync]', e);
      }
    };
  }
  function lsGet(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (e) { /* private mode */ } }

  function currentCountry() {
    // Liquid {{ localization.country.iso_code }} is the source of truth (Orbe's
    // POST to /localization reloads the page); localStorage is only a backup.
    return (root.getAttribute('data-country') || lsGet(STORAGE_KEY) || '').toUpperCase();
  }

  /* ---------- BA (bacurr) helpers ------------------------------------------
     NOTE: BA's <option> and <li> share duplicate ids (id="USD"), so never
     select by id - use [value] / [rel]. */
  function baSelect() { return document.getElementById('baCurrSelector'); }

  function baOffers(code) {
    var s = baSelect();
    return !!(s && s.querySelector('option[value="' + code + '"]'));
  }

  function baActive() {
    var label = document.querySelector('.bacurr-choiceDesign .ba-chosen');
    var m = label && label.textContent.toUpperCase().match(/\b[A-Z]{3}\b/);
    if (m) return m[0];
    var s = baSelect();
    return s && s.value ? String(s.value).toUpperCase() : '';
  }

  function baSessionCurrency() {
    try { return (JSON.parse(sessionStorage.getItem(BA_SESSION_KEY)) || '').toUpperCase(); } catch (e) { return ''; }
  }

  function targetCurrency() {
    var country = currentCountry();
    if (!country) return shopCurrency;
    var wanted = CURRENCY_BY_COUNTRY[country] || FALLBACK_CURRENCY;
    if (baSelect() && !baOffers(wanted)) wanted = FALLBACK_CURRENCY;
    return wanted;
  }

  // BA also auto-geolocates (auto_loc), so the label alone isn't enough: compare
  // the label AND BA's stored choice with the target.
  function needsSwitch(target) {
    if (baActive() !== target) return true;
    var stored = baSessionCurrency();
    return stored ? stored !== target : target !== shopCurrency;
  }

  // Header button label + "approximate prices" cart note (snippets/ccs-checkout-note.liquid).
  function updateLabel(code) {
    if (!code) return;
    var i;
    var labels = document.querySelectorAll('[data-ccs-label], [data-ccs-note-currency]');
    for (i = 0; i < labels.length; i++) {
      if (labels[i].textContent !== code) labels[i].textContent = code;
    }
    var notes = document.querySelectorAll('[data-ccs-checkout-note]');
    for (i = 0; i < notes.length; i++) notes[i].hidden = (code === shopCurrency);
  }

  /* Product add-ons ("Box Packaging (Rs. 500)", "Lining + Rs. 2000") are rendered by
     the product options app as plain label text. Wrap just the visible amount in the
     same <span class="money"> markup BA converts on product prices. The checkbox
     value (the line-item property) is left untouched. Idempotent: wrapped amounts
     live inside the span, so they're never matched again. */
  var ADDON_INPUTS = 'label > input[type="checkbox"].spb-productoptiontextcolor';
  var RS_AMOUNT = /Rs\.?\s*([\d,]+(?:\.\d+)?)/i;

  function wrapAddonPrices() {
    if (baActive() === shopCurrency) return 0; // nothing to convert in base currency
    var wrapped = 0;
    var inputs = document.querySelectorAll(ADDON_INPUTS);
    for (var i = 0; i < inputs.length; i++) {
      var nodes = inputs[i].parentNode.childNodes;
      for (var j = nodes.length - 1; j >= 0; j--) {
        var node = nodes[j];
        var m = node.nodeType === 3 && node.textContent.match(RS_AMOUNT);
        if (!m) continue;
        var span = document.createElement('span');
        span.className = 'money';
        span.textContent = 'Rs.' + m[1];
        var text = node.textContent;
        node.parentNode.insertBefore(document.createTextNode(text.slice(0, m.index)), node);
        node.parentNode.insertBefore(span, node);
        node.textContent = text.slice(m.index + m[0].length);
        wrapped += 1;
      }
    }
    return wrapped;
  }

  function refreshConversion() {
    var ba = window.baCurr;
    if (ba && typeof ba.refreshConversion === 'function' && baActive() !== shopCurrency) {
      ba.refreshConversion();
    }
  }

  /* ---------- 1. Sync BA to the country's currency ------------------------- */
  var switchBusyUntil = 0;

  function switchTo(code) {
    switchBusyUntil = Date.now() + 700;
    // Preferred: BA's own list item -> runs BA's click handler (converts + stores choice).
    var li = document.querySelector('ul.bacurr-dropdown-choices li.currMovers[rel="' + code + '"]');
    if (li) li.click();

    setTimeout(safe(function () {
      if (baActive() !== code) {
        // Fallback: native select + change event.
        var s = baSelect();
        if (s) {
          s.value = code;
          s.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      updateLabel(baActive() || code);
    }), 500);
  }

  function startSync() {
    var attempts = 0;
    var pending = null;
    var observer = null;

    function stop() {
      if (observer) { observer.disconnect(); observer = null; }
      if (!baSelect()) updateLabel(shopCurrency); // BA never loaded: prices stay in base currency
    }

    var check = safe(function () {
      pending = null;
      if (!baSelect() || Date.now() < switchBusyUntil) return;
      var target = targetCurrency();
      if (!needsSwitch(target)) {
        updateLabel(target);
        if (wrapAddonPrices()) refreshConversion(); // add-ons rendered by the options app
        return;
      }
      if (attempts >= MAX_SWITCH_ATTEMPTS) { updateLabel(baActive()); stop(); return; }
      attempts += 1;
      switchTo(target);
    });

    function schedule() {
      if (!pending) pending = setTimeout(check, 200);
    }

    // Returning page view: BA already holds the right currency -> show it immediately.
    if (baSessionCurrency() === targetCurrency()) updateLabel(baSessionCurrency());

    // Watch for the BA widget (and for BA's auto-geo overriding us right after init),
    // then disconnect after WAIT_FOR_BA_MS.
    if ('MutationObserver' in window) {
      observer = new MutationObserver(schedule);
      observer.observe(document.body, { childList: true, subtree: true });
    }
    setTimeout(safe(stop), WAIT_FOR_BA_MS);
    schedule();
  }

  /* ---------- 2. Re-convert prices injected by AJAX ------------------------- */
  function bindReconvertHooks() {
    var timer = null;
    var scheduleRefresh = function () {
      clearTimeout(timer);
      timer = setTimeout(safe(function () {
        wrapAddonPrices(); // options app may re-render add-ons on variant change
        refreshConversion();
        setTimeout(safe(refreshConversion), 700); // second pass for late renders (cart drawer)
      }), 120);
    };

    // Impulse theme events (capture: some are dispatched on containers without bubbling).
    ['cart:updated', 'ajaxProduct:added', 'drawerOpen', 'quickview:loaded', 'quickview:open',
      'collection:reloaded', 'recommendations:loaded', 'variantPriceChange', 'variant:change'
    ].forEach(function (name) {
      document.addEventListener(name, scheduleRefresh, true);
    });

    // Containers the theme re-renders asynchronously with no "done" event:
    // predictive search results, and the cart drawer/page items + subtotal (rebuilt
    // after the /cart fetch, later than cart:updated / ajaxProduct:added fire).
    // Direct children only; refreshes triggered here are rate-limited so BA's own
    // re-render of these nodes can't loop.
    if ('MutationObserver' in window) {
      var lastObserved = 0;
      var onRender = function () {
        if (Date.now() - lastObserved < 1500) return;
        lastObserved = Date.now();
        scheduleRefresh();
      };
      var targets = document.querySelectorAll('[id="predictive-search"], [data-products], [data-subtotal]');
      for (var i = 0; i < targets.length; i++) {
        new MutationObserver(onRender).observe(targets[i], { childList: true });
      }
    }
  }

  /* ---------- 3. Orbe popup: open / close ---------------------------------- */
  var fallbackOpenActive = false;

  function getModal() { return document.getElementById(ORBE_MODAL_ID); }
  function isOpen(modal) { return modal && !modal.classList.contains('orbe--hidden') && !modal.classList.contains('orbe--closing'); }
  function canClose(modal) {
    var btn = modal.querySelector('.md-modal-closeButtonAction');
    return !btn || btn.style.display !== 'none'; // Orbe hides the close button when a choice is forced
  }

  function openOrbe(trigger) {
    var modal = getModal();
    if (!modal) return;
    window.lastModalTriggerButton = trigger; // Orbe returns focus here on close

    // a) Orbe's instance method: syncs the selector to the current country, shows the
    //    close button and opens - it ignores Orbe's "already shown" cookie.
    try {
      /* global orbito */
      if (typeof orbito !== 'undefined' && orbito && typeof orbito.openModalWithoutRefresh === 'function') {
        orbito.openModalWithoutRefresh(true);
        return;
      }
    } catch (e) { /* try next */ }

    // b) Orbe's public global.
    try {
      if (typeof window.mdApp_openModal === 'function') {
        window.mdApp_openModal(ORBE_MODAL_ID);
        return;
      }
    } catch (e) { /* try next */ }

    // c) Fallback: replicate Orbe's open (same classes/attributes).
    fallbackOpenActive = true;
    document.body.classList.add('orbe--opened');
    modal.classList.remove('orbe--hidden');
    modal.setAttribute('aria-hidden', 'false');
    var dialog = modal.querySelector('[role="dialog"]');
    if (dialog) dialog.focus();
  }

  function closeOrbe() {
    var modal = getModal();
    if (!isOpen(modal) || !canClose(modal)) return;

    if (!fallbackOpenActive) {
      // Click Orbe's own close button so its "dismissed" cookie logic also runs.
      var closeBtn = modal.querySelector('.md-modal-closeButtonAction');
      if (closeBtn) { closeBtn.click(); return; }
      if (typeof window.mdApp_closeModal === 'function') { window.mdApp_closeModal(ORBE_MODAL_ID); return; }
    }
    fallbackOpenActive = false;
    modal.classList.add('orbe--hidden');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('orbe--opened');
    var t = window.lastModalTriggerButton;
    if (t && typeof t.focus === 'function') t.focus();
  }

  function rememberCountry() {
    var toggle = document.getElementById('md-form__select__country');
    var input = document.getElementById('orbe-country-selector-popup-id');
    var code = (toggle && toggle.getAttribute('data-value')) || (input && input.value) || '';
    if (/^[A-Za-z]{2}$/.test(code)) lsSet(STORAGE_KEY, code.toUpperCase());
  }

  function bindUi() {
    var modal = getModal();
    var buttons = document.querySelectorAll('.js-ccs-open');

    // No Orbe embed on this page: hide the dead button.
    if (!modal) {
      for (var i = 0; i < buttons.length; i++) buttons[i].style.display = 'none';
      return;
    }

    document.addEventListener('click', safe(function (evt) {
      var t = evt.target;
      if (!t || !t.closest) return;

      var trigger = t.closest('.js-ccs-open');
      if (trigger) { evt.preventDefault(); openOrbe(trigger); return; }

      if (t.closest('#md-btn__form__onSubmit')) { rememberCountry(); return; }

      if (!isOpen(modal)) return;
      // Backdrop click: Orbe's full-screen container and full-width dialog sit above
      // .md-modal__backdrop, so treat any click in the modal outside the visible card
      // (.md-modal__content) as a backdrop click. Orbe has no handler for this.
      if (modal.contains(t) && !t.closest('.md-modal__content')) {
        closeOrbe();
        return;
      }
      // Close button: Orbe handles it itself; only needed for the fallback open.
      if (fallbackOpenActive && t.closest('.md-modal-closeButtonAction')) closeOrbe();
    }));

    document.addEventListener('submit', safe(function (evt) {
      if (evt.target && evt.target.id === 'md-modal__form__id') rememberCountry();
    }), true);

    // Esc: Orbe listens on the modal itself; cover focus-outside-modal and the fallback open.
    document.addEventListener('keydown', safe(function (evt) {
      if (evt.key !== 'Escape' && evt.key !== 'Esc') return;
      if (!isOpen(modal)) return;
      if (fallbackOpenActive || !modal.contains(document.activeElement)) closeOrbe();
    }));
  }

  /* ---------- Init ---------------------------------------------------------- */
  var init = safe(function () {
    safe(bindUi)();
    safe(bindReconvertHooks)();
    safe(startSync)();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
